/**
 * Hydrogen Anchored Positioning
 *
 * Places a floating element against a trigger, in the viewport rather than in
 * the trigger's scroll container.
 *
 * Static CSS offsets — `top: calc(100% + 4px)` on an absolutely positioned
 * panel — are enough right up until the trigger is near an edge or inside
 * something that clips. Then the panel is cut off, or the browser scrolls the
 * clipping ancestor to reveal it and the surrounding UI jumps. Both failures
 * look like styling bugs and are actually the absence of a measurement.
 *
 * So the geometry is computed from the two boxes involved: the preferred side
 * is used when it fits, a fallback side when it does not, and the cross axis
 * is shifted to keep the panel inside the viewport. The result is written as
 * custom properties (and optionally as a fixed-position transform), which
 * leaves the declarations in CSS where a stylesheet can still override them.
 *
 * The feature set matches what Radix Popper exposes through Floating UI:
 * `side`/`align`, `sideOffset`/`alignOffset`, per-side collision padding,
 * `avoidCollisions`, flip with a fallback order, partial/always sticky shift,
 * an arrow position, available width/height and a transform origin as CSS
 * variables (named by the caller, so a converted `--radix-*` rule can be
 * pointed at them), a virtual anchor for pointer-positioned menus, and
 * auto-update on scroll, resize and element resize.
 *
 * @module anchor
 * @example
 * panel.hidden = false;
 * const detach = attachAnchoredPosition(panel, trigger, { side: 'bottom' });
 * // later
 * detach();
 *
 * @example Radix-style wrapper positioned with a transform
 * const position = createAnchoredPosition(wrapper, trigger, {
 *   content, apply: 'fixed', side: 'right', align: 'start', sideOffset: 4,
 *   vars: { transformOrigin: '--radix-popover-content-transform-origin' },
 * });
 * position.update();
 * position.destroy();
 */
import { warnOnce } from '../utils/errors.js';
const OPPOSITE = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
};
/** A virtual anchor for a point, e.g. the pointer of a context menu. */
export function pointAnchor(x, y) {
    return {
        getBoundingClientRect: () => ({
            top: y,
            right: x,
            bottom: y,
            left: x,
            width: 0,
            height: 0,
        }),
    };
}
function padsOf(value) {
    if (typeof value === 'number')
        return { top: value, right: value, bottom: value, left: value };
    return {
        top: value.top ?? 0,
        right: value.right ?? 0,
        bottom: value.bottom ?? 0,
        left: value.left ?? 0,
    };
}
/**
 * True when an element can actually be measured.
 *
 * Positioning is an enhancement over the stylesheet's static placement, so a
 * node with no layout — a server render, a test double standing in for one —
 * has to leave it alone rather than throw. The CSS fallback still applies,
 * because it is only overridden once a measurement exists.
 */
function isMeasurable(target) {
    return typeof target.getBoundingClientRect === 'function';
}
function isMeasurablePanel(element) {
    return (isMeasurable(element) &&
        typeof element.ownerDocument === 'object' &&
        element.ownerDocument !== null);
}
function boxOf(target) {
    const rect = target.getBoundingClientRect();
    return {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height,
    };
}
/** Viewport size in CSS pixels, which is what fixed positioning is relative to. */
function viewportOf(element) {
    const view = element.ownerDocument.defaultView;
    const documentElement = element.ownerDocument.documentElement;
    return {
        width: view?.innerWidth ?? documentElement?.clientWidth ?? 0,
        height: view?.innerHeight ?? documentElement?.clientHeight ?? 0,
    };
}
/** Space between the anchor and the (padded) viewport edge on one side. */
function roomOn(side, anchor, viewport, pads) {
    switch (side) {
        case 'top':
            return anchor.top - pads.top;
        case 'bottom':
            return viewport.height - anchor.bottom - pads.bottom;
        case 'left':
            return anchor.left - pads.left;
        default:
            return viewport.width - anchor.right - pads.right;
    }
}
/** Keeps a coordinate inside the padded viewport, favouring the near edge. */
function clamp(value, size, limit, start, end) {
    const max = limit - end - size;
    if (max < start)
        return start;
    return Math.min(Math.max(value, start), max);
}
/** Cross-axis start coordinate for an alignment, with the Radix `alignOffset` sign. */
function alignedStart(align, anchorStart, anchorSize, panelSize, alignOffset, reversed) {
    // In a reversed (RTL, horizontal) axis `start` is the far edge.
    const effective = reversed && align !== 'center' ? (align === 'start' ? 'end' : 'start') : align;
    const nudge = alignOffset * (reversed ? -1 : 1) * (align === 'end' ? -1 : 1);
    if (effective === 'center')
        return anchorStart + (anchorSize - panelSize) / 2 + nudge;
    if (effective === 'end')
        return anchorStart + anchorSize - panelSize + nudge;
    return anchorStart + nudge;
}
/**
 * Computes where the panel goes, without touching the DOM.
 *
 * Exported because the decision is worth testing on its own: the flip and the
 * shift are the parts that a hand-written offset gets wrong, and they are pure
 * arithmetic over two boxes.
 */
export function resolveAnchorPlacement(anchor, panel, viewport, options = {}) {
    const preferred = options.side ?? 'bottom';
    const align = options.align ?? 'start';
    const sideOffset = options.sideOffset ?? options.offset ?? 4;
    const alignOffset = options.alignOffset ?? 0;
    const pads = padsOf(options.collisionPadding ?? options.padding ?? 8);
    const avoid = options.avoidCollisions !== false;
    const arrowSize = options.arrowSize;
    const rtl = options.rtl === true;
    const gap = sideOffset + (arrowSize ? arrowSize.height : 0);
    const panelMain = (side) => side === 'top' || side === 'bottom' ? panel.height : panel.width;
    const overflowOn = (side) => panelMain(side) + gap - roomOn(side, anchor, viewport, pads);
    let side = preferred;
    if (avoid) {
        const candidates = [preferred];
        for (const fallback of options.fallbackSides ?? [OPPOSITE[preferred]]) {
            if (!candidates.includes(fallback))
                candidates.push(fallback);
        }
        let best = overflowOn(preferred);
        // The first side that fits wins; otherwise the least-bad one, and only
        // when strictly better than the preferred side, so a panel that fits
        // nowhere stays where the caller put it and is clamped instead.
        if (best > 0) {
            for (const candidate of candidates.slice(1)) {
                const overflow = overflowOn(candidate);
                if (overflow < best) {
                    best = overflow;
                    side = candidate;
                    if (overflow <= 0)
                        break;
                }
            }
        }
    }
    const flipped = side !== preferred;
    const vertical = side === 'top' || side === 'bottom';
    let x;
    let y;
    if (vertical) {
        y = side === 'bottom' ? anchor.bottom + gap : anchor.top - panel.height - gap;
        x = alignedStart(align, anchor.left, anchor.width, panel.width, alignOffset, rtl);
    }
    else {
        x = side === 'right' ? anchor.right + gap : anchor.left - panel.width - gap;
        y = alignedStart(align, anchor.top, anchor.height, panel.height, alignOffset, false);
    }
    if (avoid) {
        const crossStart = vertical ? x : y;
        const crossSize = vertical ? panel.width : panel.height;
        const crossLimit = vertical ? viewport.width : viewport.height;
        const padStart = vertical ? pads.left : pads.top;
        const padEnd = vertical ? pads.right : pads.bottom;
        let shifted = clamp(crossStart, crossSize, crossLimit, padStart, padEnd);
        if (options.sticky !== 'always') {
            // limitShift: never let the panel drift so far that it no longer touches the anchor.
            const anchorStart = vertical ? anchor.left : anchor.top;
            const anchorSize = vertical ? anchor.width : anchor.height;
            const lowest = anchorStart - crossSize;
            const highest = anchorStart + anchorSize;
            shifted = Math.min(Math.max(shifted, Math.min(lowest, crossStart)), Math.max(highest, crossStart));
        }
        if (vertical) {
            x = shifted;
            y = clamp(y, panel.height, viewport.height, pads.top, pads.bottom);
        }
        else {
            y = shifted;
            x = clamp(x, panel.width, viewport.width, pads.left, pads.right);
        }
    }
    // Available room, as Floating UI's `size` middleware reports it.
    const viewportWidth = viewport.width - pads.left - pads.right;
    const viewportHeight = viewport.height - pads.top - pads.bottom;
    let availableWidth = viewportWidth;
    let availableHeight = viewportHeight;
    if (side === 'bottom')
        availableHeight = roomOn('bottom', anchor, viewport, pads) - gap;
    else if (side === 'top')
        availableHeight = roomOn('top', anchor, viewport, pads) - gap;
    else if (side === 'right')
        availableWidth = roomOn('right', anchor, viewport, pads) - gap;
    else
        availableWidth = roomOn('left', anchor, viewport, pads) - gap;
    // Arrow: centred on the anchor along the cross axis, kept off the corners.
    let arrow = null;
    let arrowHidden = false;
    let arrowCenter = 0;
    if (arrowSize) {
        const arrowPadding = options.arrowPadding ?? 0;
        const arrowLength = vertical ? arrowSize.width : arrowSize.height;
        const panelLength = vertical ? panel.width : panel.height;
        const panelStart = vertical ? x : y;
        const anchorCenter = vertical ? anchor.left + anchor.width / 2 : anchor.top + anchor.height / 2;
        const ideal = anchorCenter - panelStart - arrowLength / 2;
        const maxOffset = panelLength - arrowLength - arrowPadding;
        const clamped = Math.min(Math.max(ideal, arrowPadding), Math.max(arrowPadding, maxOffset));
        arrowHidden = clamped !== ideal;
        arrowCenter = clamped + arrowLength / 2;
        arrow = vertical ? { x: clamped, y: null } : { x: null, y: clamped };
    }
    const keyword = { start: '0%', center: '50%', end: '100%' }[align];
    const arrowDepth = arrowSize ? arrowSize.height : 0;
    let originX;
    let originY;
    if (side === 'bottom') {
        originX = arrowSize ? `${arrowCenter}px` : keyword;
        originY = `${-arrowDepth}px`;
    }
    else if (side === 'top') {
        originX = arrowSize ? `${arrowCenter}px` : keyword;
        originY = `${panel.height + arrowDepth}px`;
    }
    else if (side === 'right') {
        originX = `${-arrowDepth}px`;
        originY = arrowSize ? `${arrowCenter}px` : keyword;
    }
    else {
        originX = `${panel.width + arrowDepth}px`;
        originY = arrowSize ? `${arrowCenter}px` : keyword;
    }
    return {
        side,
        align,
        flipped,
        x,
        y,
        anchorWidth: anchor.width,
        anchorHeight: anchor.height,
        availableWidth: Math.max(0, availableWidth),
        availableHeight: Math.max(0, availableHeight),
        transformOrigin: `${originX} ${originY}`,
        arrow,
        arrowHidden,
    };
}
const DEFAULT_VARS = {
    anchorWidth: '--anchor-width',
    anchorHeight: '--anchor-height',
    availableWidth: '--available-width',
    availableHeight: '--available-height',
    transformOrigin: '--transform-origin',
};
function isRtl(element) {
    const dir = element.closest?.('[dir]')?.getAttribute('dir');
    if (dir)
        return dir === 'rtl';
    const view = element.ownerDocument.defaultView;
    return view?.getComputedStyle?.(element)?.direction === 'rtl';
}
function px(value) {
    return `${Math.round(value)}px`;
}
/**
 * Positions `panel` against `anchor` and records the placement on the element.
 *
 * Writes `--hydrogen-anchor-x` / `--hydrogen-anchor-y` on `panel`, and on
 * `options.content ?? panel` the resolved `data-side` / `data-align` plus the
 * measurement variables (see {@link AnchorVarNames}), so the stylesheet decides
 * what to do with the numbers — including which direction an entrance
 * animation travels.
 *
 * The panel must be measurable when this runs: a `hidden` or `display: none`
 * element reports a zero box and would be positioned as if it had no size.
 */
export function positionAnchored(panel, anchor, options = {}) {
    if (!panel || !anchor) {
        warnOnce('anchor-position-null', 'positionAnchored() called with a null panel or anchor');
        return null;
    }
    if (!isMeasurablePanel(panel) || !isMeasurable(anchor))
        return null;
    const panelBox = boxOf(panel);
    const arrowBox = options.arrow ? boxOf(options.arrow) : null;
    const placement = resolveAnchorPlacement(boxOf(anchor), { width: panelBox.width, height: panelBox.height }, viewportOf(panel), {
        ...options,
        rtl: options.rtl ?? isRtl(panel),
        arrowSize: arrowBox ? { width: arrowBox.width, height: arrowBox.height } : undefined,
    });
    const target = options.content ?? panel;
    panel.style.setProperty('--hydrogen-anchor-x', px(placement.x));
    panel.style.setProperty('--hydrogen-anchor-y', px(placement.y));
    if (options.apply === 'fixed') {
        panel.style.position = 'fixed';
        panel.style.left = '0px';
        panel.style.top = '0px';
        panel.style.minWidth = 'max-content';
        panel.style.transform = `translate3d(${px(placement.x)}, ${px(placement.y)}, 0)`;
    }
    target.dataset['side'] = placement.side;
    target.dataset['align'] = placement.align;
    if (options.vars !== false) {
        const names = options.vars ?? {};
        const style = target.style;
        style.setProperty(names.anchorWidth ?? DEFAULT_VARS.anchorWidth, px(placement.anchorWidth));
        style.setProperty(names.anchorHeight ?? DEFAULT_VARS.anchorHeight, px(placement.anchorHeight));
        style.setProperty(names.availableWidth ?? DEFAULT_VARS.availableWidth, px(placement.availableWidth));
        style.setProperty(names.availableHeight ?? DEFAULT_VARS.availableHeight, px(placement.availableHeight));
        style.setProperty(names.transformOrigin ?? DEFAULT_VARS.transformOrigin, placement.transformOrigin);
    }
    if (options.arrow && placement.arrow) {
        const { style } = options.arrow;
        style.left = placement.arrow.x === null ? '' : px(placement.arrow.x);
        style.top = placement.arrow.y === null ? '' : px(placement.arrow.y);
        style.visibility = placement.arrowHidden ? 'hidden' : '';
    }
    return placement;
}
/**
 * Keeps a panel anchored while it is open, and lets the caller re-measure on
 * demand (after content changes size, for example).
 *
 * Scroll is listened for in the capture phase because the element that scrolls
 * is usually not the one that emits a bubbling event — a pane, a list, the
 * document — and any of them moves the anchor. Resize of the window and of the
 * panel, anchor and content elements re-measures too.
 */
export function createAnchoredPosition(panel, anchor, options = {}) {
    const inert = { update: () => null, destroy: () => { } };
    if (!panel || !anchor) {
        warnOnce('anchor-attach-null', 'attachAnchoredPosition() called with a null panel or anchor');
        return inert;
    }
    if (!isMeasurablePanel(panel) || !isMeasurable(anchor))
        return inert;
    const view = panel.ownerDocument.defaultView;
    const update = () => positionAnchored(panel, anchor, options);
    const onViewportChange = () => {
        update();
    };
    update();
    view?.addEventListener('scroll', onViewportChange, true);
    view?.addEventListener('resize', onViewportChange);
    let observer = null;
    if (typeof ResizeObserver === 'function') {
        observer = new ResizeObserver(onViewportChange);
        observer.observe(panel);
        if (options.content && options.content !== panel)
            observer.observe(options.content);
        if (anchor instanceof Element)
            observer.observe(anchor);
        else if (anchor.contextElement)
            observer.observe(anchor.contextElement);
    }
    let destroyed = false;
    return {
        update,
        destroy: () => {
            if (destroyed)
                return;
            destroyed = true;
            view?.removeEventListener('scroll', onViewportChange, true);
            view?.removeEventListener('resize', onViewportChange);
            observer?.disconnect();
        },
    };
}
/**
 * Keeps a panel anchored while it is open.
 *
 * @returns A cleanup function that removes the listeners.
 */
export function attachAnchoredPosition(panel, anchor, options = {}) {
    const position = createAnchoredPosition(panel, anchor, options);
    return position.destroy;
}
//# sourceMappingURL=anchor.js.map